import { useState } from 'react'
import { weddingInfo } from '../weddingInfo'

interface CodeGateProps {
  /**
   * What to open the field with. Set when a code arrived on a link and was
   * turned down, so the guest can see what was tried instead of starting over.
   */
  initialCode?: string
  onSubmit: (code: string) => void
  submitting: boolean
  error: string | null
}

/** The front door: a single field for the code printed on the invite. */
export function CodeGate({ initialCode = '', onSubmit, submitting, error }: CodeGateProps) {
  const [code, setCode] = useState(initialCode)

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (submitting || code.trim() === '') return
    onSubmit(code)
  }

  return (
    <main className="flex min-h-dvh items-center justify-center px-4 py-12">
      <div className="animate-reveal w-full max-w-md">
        <header className="text-center">
          <p className="text-ink-soft text-xs tracking-[0.35em] uppercase">
            {weddingInfo.dateShort}
          </p>
          <h1 className="font-display text-ink mt-4 text-4xl sm:text-5xl">
            {weddingInfo.couple}
          </h1>
          <p className="text-ink-soft mt-4 text-sm">
            Please enter the code from your invitation.
          </p>
        </header>

        <form onSubmit={handleSubmit} className="mt-8" noValidate>
          <label htmlFor="code" className="text-ink-soft block text-xs tracking-widest uppercase">
            Invite code
          </label>
          <input
            id="code"
            name="code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            disabled={submitting}
            autoFocus
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            aria-describedby={error ? 'code-error' : 'code-hint'}
            aria-invalid={error !== null}
            className="border-ink/20 bg-parchment/60 focus:border-claret mt-2 w-full rounded-md border px-4 py-3 text-center font-mono text-lg tracking-[0.3em] uppercase transition-colors disabled:opacity-60"
          />

          {error ? (
            <p id="code-error" role="alert" className="text-claret mt-3 text-sm">
              {error}
            </p>
          ) : (
            <p id="code-hint" className="text-ink-soft mt-3 text-xs">
              It is on the bottom of your invitation. Capitals and dashes do not matter.
            </p>
          )}

          <button
            type="submit"
            disabled={submitting || code.trim() === ''}
            className="bg-claret hover:bg-claret-dark mt-6 w-full rounded-md px-4 py-3 text-sm tracking-widest text-white uppercase transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Checking…' : 'Open our invitation'}
          </button>
        </form>
      </div>
    </main>
  )
}
